import { isServerError } from 'api/BaseApiClient';
import type { BackendErrorResponse } from 'api/types';

export type ParsedFieldError = {
  path: string;
  message: string;
};

export type ParsedBackendErrors = {
  fieldErrors: ParsedFieldError[];
  unmappedMessages: string[];
};

const SEGMENT_PATTERN = /^[a-z][a-z0-9_]*$/;
const SEPARATOR = ': ';
// Matches a sibling-key boundary: whitespace, then snake_case, then ": ".
// Citadel's _errors_iter joins sibling dict entries with " " (single space), producing strings like
// "subject: ['msg-A'] header: ['msg-B'] button: ['msg-C']" when multiple peers under the same
// parent fail at once.
const SIBLING_BOUNDARY = /\s([a-z][a-z0-9_]*): /g;

// Citadel sometimes stringifies a Python list of errors (e.g. "['Too long']") into the leaf message.
// Strip the wrapper so the user sees a clean sentence.
const PY_LIST_REPR = /^\['(.*)'\]$/s;

const cleanMessage = (raw: string): string => {
  const trimmed = raw.trim();
  const listMatch = trimmed.match(PY_LIST_REPR);

  return listMatch ? listMatch[1].trim() : trimmed;
};

// A snake_case `: ` match inside a value-part is a real sibling boundary only when the character
// immediately before the boundary's whitespace looks like the end of a leaf — i.e. punctuation or
// a bracket, not a letter/digit. This filters out cases like "Invalid sources for employment: [...]"
// where "employment:" sits inside prose, while still catching `['msg-A'] header: ` style fan-outs.
const isLeafEndingChar = (char: string): boolean => char !== '' && !/[A-Za-z0-9_]/.test(char);

// Recursively walk a chained message under `parentPath`. Returns one entry per leaf error.
// Handles three shapes citadel emits within a single invalid-params message:
//   1. Single leaf:                     "support_email: Enter a valid email."
//   2. Deep single chain:               "first_email: header: ['Too long']"
//   3. Sibling fan-out:                 "subject: ['msg-A'] header: ['msg-B']" (peers under the
//                                       just-descended key)
const parseChain = (parentPath: string, message: string): ParsedFieldError[] => {
  const colonIdx = message.indexOf(SEPARATOR);
  if (colonIdx === -1) {
    return [{ path: parentPath, message: cleanMessage(message) }];
  }

  const leadingKey = message.slice(0, colonIdx);
  if (!SEGMENT_PATTERN.test(leadingKey)) {
    return [{ path: parentPath, message: cleanMessage(message) }];
  }

  const candidate = `${parentPath}.${leadingKey}`;
  const valuePart = message.slice(colonIdx + SEPARATOR.length);

  // Find sibling boundaries inside valuePart: positions where the previous block has ended
  // (last char is non-alphanumeric) and a new snake_case key starts.
  const boundaries: number[] = [0];
  SIBLING_BOUNDARY.lastIndex = 0;
  let match: RegExpExecArray | null = SIBLING_BOUNDARY.exec(valuePart);
  while (match !== null) {
    const prevChar = valuePart.charAt(match.index - 1);
    if (isLeafEndingChar(prevChar)) {
      // +1 to skip the leading whitespace character matched by \s.
      boundaries.push(match.index + 1);
    }
    match = SIBLING_BOUNDARY.exec(valuePart);
  }
  boundaries.push(valuePart.length);

  const results: ParsedFieldError[] = [];
  for (let i = 0; i < boundaries.length - 1; i += 1) {
    const block = valuePart.slice(boundaries[i], boundaries[i + 1]).trim();
    if (!block) {
      continue;
    }

    // All blocks (the first descent and any siblings detected inside valuePart) are children of
    // `leadingKey` — citadel only emits one top-level key per invalid-params message, so any
    // additional `<key>: ` we found inside valuePart sits one level deeper than parentPath.
    results.push(...parseChain(candidate, block));
  }

  return results;
};

// Walks the flat field→string[] shape. Plain object values are recursed into and emitted as
// dotted paths so nested groups like `loan_officer: { email: [...] }` surface as `loan_officer.email`.
const collectFlatFieldErrors = (errorData: unknown, prefix = ''): ParsedFieldError[] => {
  if (!errorData || typeof errorData !== 'object') {
    return [];
  }
  const result: ParsedFieldError[] = [];
  for (const [key, value] of Object.entries(errorData as Record<string, unknown>)) {
    if (key === 'code') {
      continue;
    }
    const path = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      result.push({ path, message: cleanMessage(value[0]) });
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      result.push(...collectFlatFieldErrors(value, path));
    }
  }
  return result;
};

// Some endpoints (logo upload, request-coverage, verification, etc.) return a flat
// field→string[] shape instead of the standard `error.extra['invalid-params']` envelope:
//   { company_website: ["Enter a valid URL."], code: "invalid" }
// Detected by: top-level object with no `error` key. Nested plain-object values are walked.
const parseFlatFieldShape = (errorData: unknown): ParsedFieldError[] | null => {
  if (!errorData || typeof errorData !== 'object' || 'error' in errorData) {
    return null;
  }
  const errors = collectFlatFieldErrors(errorData);
  return errors.length > 0 ? errors : null;
};

export const parseBackendInvalidParams = (errorData: BackendErrorResponse | undefined | null): ParsedBackendErrors => {
  const flatFieldErrors = parseFlatFieldShape(errorData);
  if (flatFieldErrors) {
    return { fieldErrors: flatFieldErrors, unmappedMessages: [] };
  }

  const fieldErrors: ParsedFieldError[] = [];
  const unmappedMessages: string[] = [];

  const invalidParams = errorData?.error?.extra?.['invalid-params'] ?? [];

  for (const param of invalidParams) {
    const rawMessage = param?.message ?? '';
    const field = param?.field?.trim();

    if (!field) {
      if (rawMessage) {
        unmappedMessages.push(cleanMessage(rawMessage));
      }
      continue;
    }

    if (!rawMessage) {
      fieldErrors.push({ path: field, message: '' });
      continue;
    }

    fieldErrors.push(...parseChain(field, rawMessage));
  }

  return { fieldErrors, unmappedMessages };
};

// For mutations with no form to attach field errors to (deletes, row actions): first message wins.
export const getBackendErrorMessage = (error: BackendErrorResponse | undefined | null, fallback: string): string => {
  // A 5xx body is arbitrary server output — tracebacks, upstream hostnames — not user-facing.
  if (isServerError(error)) {
    return fallback;
  }

  const { fieldErrors, unmappedMessages } = parseBackendInvalidParams(error);

  // Messages can clean down to empty; skipping them stops one shadowing a real message behind it.
  return (
    fieldErrors.find(({ message }) => message)?.message ||
    unmappedMessages.find(Boolean) ||
    error?.error?.message ||
    fallback
  );
};
